import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Logo } from '@vd/ui';
import { useAuth } from '../features/auth/AuthProvider';
import { post } from '../lib/apiClient';

/**
 * OAuth consent screen for the Chrome extension (BACKEND_PRD §6.1).
 *
 * The extension opens this URL via `chrome.identity.launchWebAuthFlow`. The
 * user sees a confirmation ("grant extension access?") and on approval we
 * mint a one-time code via `POST /api/v1/oauth/extension/authorize`, then
 * redirect back to `redirect_uri` with `?code=<code>&state=<state>`.
 *
 * Validation is belt-and-braces: the server also validates every param
 * (PKCE challenge format, redirect scheme, client_id). Rejecting here just
 * saves a round-trip on obviously-broken params.
 */

interface ParsedParams {
  clientId: string;
  state: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  redirectUri: string;
}

// `chrome.identity.launchWebAuthFlow` returns `https://<ext-id>.chromiumapp.org/<path>`
// on Chrome/Edge/Brave (not `chrome-extension://`). Firefox uses the same host
// pattern; forks may also pass `moz-extension://`. Keep server + client
// validators in sync — duplicated here to fail fast before the authorize call.
const REDIRECT_RE =
  /^(?:(?:chrome-extension|moz-extension):\/\/[a-z0-9]{16,}\/[A-Za-z0-9/_-]+|https:\/\/[a-z0-9]{16,}\.chromiumapp\.org\/[A-Za-z0-9/_-]+)$/;
const CHALLENGE_RE = /^[A-Za-z0-9_-]{43,128}$/;
const STATE_RE = /^[A-Za-z0-9_-]{8,200}$/;

function parse(params: URLSearchParams): { ok: true; value: ParsedParams } | { ok: false; reason: string } {
  const clientId = params.get('client_id') ?? '';
  const state = params.get('state') ?? '';
  const codeChallenge = params.get('code_challenge') ?? '';
  const codeChallengeMethod = params.get('code_challenge_method') ?? '';
  const redirectUri = params.get('redirect_uri') ?? '';

  if (clientId !== 'vd-extension') return { ok: false, reason: 'Unknown client.' };
  if (!STATE_RE.test(state)) return { ok: false, reason: 'Missing or malformed state parameter.' };
  if (!CHALLENGE_RE.test(codeChallenge)) return { ok: false, reason: 'Missing or malformed code_challenge.' };
  if (codeChallengeMethod !== 'S256') return { ok: false, reason: 'Only S256 PKCE is supported.' };
  if (!REDIRECT_RE.test(redirectUri)) return { ok: false, reason: 'Invalid redirect_uri: must be a browser extension callback.' };
  return { ok: true, value: { clientId, state, codeChallenge, codeChallengeMethod, redirectUri } };
}

async function authorize(parsed: ParsedParams): Promise<string> {
  // Not through `lib/api`, whose MSW store doesn't handle /oauth, but through
  // its transport, which names the account this page was built for: consent
  // shown to one account must not be granted to another that has since signed
  // in from another tab (review of 0.2.0, 3 Oct).
  const { code } = await post<{ code: string }>('/v1/oauth/extension/authorize', {
    clientId: parsed.clientId,
    codeChallenge: parsed.codeChallenge,
    codeChallengeMethod: parsed.codeChallengeMethod,
    redirectUri: parsed.redirectUri,
  });
  return code;
}

function redirectBack(parsed: ParsedParams, code: string) {
  const url = new URL(parsed.redirectUri);
  url.searchParams.set('code', code);
  url.searchParams.set('state', parsed.state);
  window.location.replace(url.toString());
}

function redirectDenied(parsed: ParsedParams) {
  const url = new URL(parsed.redirectUri);
  url.searchParams.set('error', 'access_denied');
  url.searchParams.set('state', parsed.state);
  window.location.replace(url.toString());
}

export function OAuthExtensionPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsed = useMemo(() => parse(params), [params]);

  // Surface the redirect target + the user's current identity so there's
  // zero ambiguity about *who* is about to be connected.
  useEffect(() => {
    if (!parsed.ok) setError(parsed.reason);
  }, [parsed]);

  // Never inside a frame (security double check, 2026-10-11): the app is a
  // static site that cannot send `frame-ancestors`, and an Approve button
  // another page can frame is what clickjacking wants. The widget's sign-in
  // page refuses the same way.
  if (window.top !== window.self) {
    return (
      <div style={wrap}>
        <Logo size={32} />
        <h1 style={h1}>Open this in its own tab</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          Toado only asks you to approve a connection in a page of its own, never inside another site.
        </p>
      </div>
    );
  }

  if (!parsed.ok) {
    return (
      <div style={wrap}>
        <Logo size={32} />
        <h1 style={h1}>Can't connect extension</h1>
        <p style={{ color: 'var(--text-secondary)' }}>{error ?? 'Invalid request.'}</p>
        <p style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
          If you reached this page from the Toado extension, try reinstalling and trying again.
        </p>
      </div>
    );
  }

  const extensionId = (() => {
    try { return new URL(parsed.value.redirectUri).hostname; } catch { return parsed.value.redirectUri; }
  })();

  const onApprove = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const code = await authorize(parsed.value);
      redirectBack(parsed.value, code);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authorization failed.');
      setSubmitting(false);
    }
  };

  return (
    <div style={wrap}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Logo size={32} />
        <div>
          <h1 style={h1}>Connect extension</h1>
          <div style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
            Signed in as <strong>{user?.email ?? '-'}</strong>
          </div>
        </div>
      </div>

      <p style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        The Toado browser extension (<code>{extensionId}</code>) is requesting access to your account.
        Approving will let it capture pages and create tickets on your behalf.
      </p>

      <ul style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.7, paddingLeft: 18, marginTop: 16 }}>
        <li>Read your projects and tickets</li>
        <li>Submit captures and walkthrough recordings, and create tickets</li>
        {/* Said here as well as in the privacy policy (TOADO-448): the one thing the
            extension sends without a capture. */}
        <li>Show your team&rsquo;s pins on sites your workspace has added, which sends each page&rsquo;s address (never its query string) to your account as you browse those sites</li>
        <li>You can disconnect any time from the extension options or your user settings</li>
      </ul>

      {/* Another extension can open this page with Toado's own address and
          read the answer from the tab (0.2.0 review), so the person is the
          last check: this page should only ever follow their own Connect. */}
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.5, marginTop: 16 }}>
        Only approve if you just pressed <strong>Connect to Toado</strong> in the extension yourself. If this page
        opened on its own, choose Deny.
      </p>

      {error && (
        <div style={{ marginTop: 16, padding: 10, borderRadius: 6, background: 'var(--danger-bg)', color: 'var(--danger)', fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
        <Button variant="primary" onClick={onApprove} disabled={submitting}>
          {submitting ? 'Approving…' : 'Approve'}
        </Button>
        <Button variant="ghost" onClick={() => redirectDenied(parsed.value)} disabled={submitting}>
          Deny
        </Button>
      </div>
    </div>
  );
}

const wrap: React.CSSProperties = {
  maxWidth: 560,
  margin: '64px auto',
  padding: '32px 28px',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  fontFamily: 'Inter, sans-serif',
  color: 'var(--text)',
};
const h1: React.CSSProperties = { fontSize: 20, margin: 0 };
